import {
  Box,
  Card,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import { CalculateRounded, RestartAltRounded } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeCourseGrade } from '@dosfilos/domain';
import { useParams } from 'react-router-dom';
import { useSectionAssignments } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import type { AssignmentSummaryDto } from '../../../lib/api/assignment.api';
import { formatDate } from '../../../lib/i18n-format';

interface GradeRow {
  assignment: AssignmentSummaryDto;
  earnedPoints: number | null; // real grade if exists
  whatIfPoints: number | null; // override
}

function effectivePoints(row: GradeRow): number | null {
  if (row.whatIfPoints !== null) return row.whatIfPoints;
  return row.earnedPoints;
}

export function CourseGradesPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { gradingScale } = useTenantConfig();
  const { sectionId } = useParams<{ sectionId: string }>();
  const { data, isLoading } = useSectionAssignments(sectionId);
  const [whatIf, setWhatIf] = useState<Record<string, number | null>>({});

  const rows: GradeRow[] = useMemo(() => {
    return (data?.items ?? [])
      .filter((a) => a.publishedAt)
      .map((a) => ({
        assignment: a,
        earnedPoints: a.studentSubmission?.finalPoints ?? null,
        whatIfPoints: whatIf[a.id] ?? null,
      }));
  }, [data, whatIf]);

  // LA MISMA CUENTA QUE USA EL SEMINARIO para cerrar el curso, no una copia.
  // Vivía sólo acá, y el día que el seminario calculara la suya por separado el
  // alumno y la institución verían números distintos del mismo curso — los dos
  // pareciendo correctos.
  //
  // Y CORRIGE UN DEFECTO DE ESTA PANTALLA: la cuenta anterior dividía por el
  // peso de TODAS las tareas publicadas, así que una tarea que nadie había
  // corregido todavía pesaba como un cero. A mitad de período, con una tarea
  // corregida de cinco, el alumno veía el 20 % de su rendimiento real. Ahora el
  // promedio se calcula sobre lo CORREGIDO, y lo pendiente se muestra aparte.
  const computed = computeCourseGrade(
    rows.map((r) => ({
      id: r.assignment.id,
      weight: r.assignment.weight,
      pointsPossible: r.assignment.pointsPossible,
    })),
    new Map(rows.map((r) => [r.assignment.id, effectivePoints(r)])),
    // Con la escala DEL SEMINARIO: el verde dice «va aprobando» según su escala,
    // no según el 60 % de la escala chilena.
    { scale: gradingScale, passingGrade: null },
  );
  const currentAvg = computed.percentCurrent;
  // COMO FRACCIÓN DEL CURSO, no como suma de pesos: los pesos no suman 100 —en
  // producción suman 70, 80 y hasta 171 según la sección— así que un peso
  // pendiente de 40 puede ser la mitad del curso o una cuarta parte.
  const pendingShare =
    computed.weightTotal > 0
      ? ((computed.weightTotal - computed.weightGraded) / computed.weightTotal) * 100
      : 0;

  if (isLoading) return <PortalLoading />;
  if (rows.length === 0) {
    return (
      <PortalEmpty title={t('portal.grades.emptyTitle')} body={t('portal.grades.emptyBody')} />
    );
  }

  return (
    <Stack spacing={2}>
      {/* Summary */}
      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="#8b949e">
              {t('portal.grades.currentAverage')}
            </Typography>
            <Typography
              variant="h3"
              fontWeight={700}
              color={computed.passed === null ? '#8b949e' : computed.passed ? '#10b981' : '#f59e0b'}
            >
              {currentAvg === null ? '—' : `${currentAvg.toFixed(1)}%`}
            </Typography>
            {/* SIN NADA CORREGIDO NO HAY PROMEDIO, y un «0 %» sería una mentira
                desalentadora. Y cuando queda trabajo sin corregir, se dice:
                el número de arriba es sobre lo revisado, no sobre el curso. */}
            {pendingShare > 0 && (
              <Typography variant="caption" color="#8b949e">
                {t('portal.grades.pendingWeight', { pct: Math.round(pendingShare) })}
              </Typography>
            )}
          </Box>
          <Tooltip title={t('portal.grades.whatIfTooltip')}>
            <IconButton sx={{ color: '#8b949e' }}>
              <CalculateRounded />
            </IconButton>
          </Tooltip>
          {Object.keys(whatIf).length > 0 && (
            <Tooltip title={t('portal.grades.resetWhatIf')}>
              <IconButton onClick={() => setWhatIf({})} sx={{ color: '#8b949e' }}>
                <RestartAltRounded />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Divider sx={{ my: 2, borderColor: '#21262d' }} />
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <CalculateRounded sx={{ color: '#a5b4fc', fontSize: 18, mt: 0.25 }} />
          <Typography variant="caption" color="#8b949e">
            <strong style={{ color: '#a5b4fc' }}>{t('portal.grades.simulatorLabel')}</strong>{' '}
            {t('portal.grades.simulatorBody')}
          </Typography>
        </Stack>
      </Card>

      {/* Grade table */}
      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 }}>
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5, borderBottom: '1px solid #21262d' }}>
          <Stack direction="row" alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="#8b949e" fontWeight={700}>
                {t('portal.grades.colAssignment')}
              </Typography>
            </Box>
            <Box sx={{ width: 70, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="caption" color="#8b949e" fontWeight={700}>
                {t('portal.grades.colWeight')}
              </Typography>
            </Box>
            <Box sx={{ width: { xs: 130, sm: 150 }, textAlign: 'right' }}>
              <Typography variant="caption" color="#a5b4fc" fontWeight={700}>
                {t('portal.grades.colSimulate')}
              </Typography>
            </Box>
          </Stack>
        </Box>
        {rows.map((row, i) => {
          const a = row.assignment;
          const real = row.earnedPoints;
          const wi = row.whatIfPoints;
          const isWhatIf = wi !== null;
          return (
            <Box
              key={a.id}
              sx={{
                px: { xs: 1.5, sm: 2.5 },
                py: 1.5,
                borderBottom: i === rows.length - 1 ? 'none' : '1px solid #21262d',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} color="#e6edf3" noWrap>
                  {a.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {a.dueAt && (
                    <Typography variant="caption" color="#8b949e">
                      {formatDate(a.dueAt)}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="#8b949e"
                    sx={{ display: { xs: 'inline', sm: 'none' } }}
                  >
                    · {a.weight}%
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ width: 70, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="caption" color="#8b949e">
                  {a.weight}%
                </Typography>
              </Box>
              <Box
                sx={{
                  width: { xs: 130, sm: 150 },
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {real !== null && (
                  <Typography
                    variant="caption"
                    color={isWhatIf ? '#6e7681' : '#10b981'}
                    sx={{ textDecoration: isWhatIf ? 'line-through' : 'none' }}
                  >
                    {real.toFixed(1)}
                  </Typography>
                )}
                <TextField
                  size="small"
                  type="number"
                  placeholder="—"
                  value={wi ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setWhatIf((prev) => {
                      const next = { ...prev };
                      if (v === '') delete next[a.id];
                      else next[a.id] = Number(v);
                      return next;
                    });
                  }}
                  inputProps={{
                    min: 0,
                    max: a.pointsPossible,
                    style: { width: 44, textAlign: 'right' },
                    'aria-label': t('portal.grades.simulateAria', { name: a.name }),
                  }}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: 13, py: 0.5 },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(165,180,252,0.35)',
                      borderStyle: 'dashed',
                    },
                  }}
                />
                <Typography variant="caption" color="#8b949e">
                  /{a.pointsPossible}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Card>

      {Object.keys(whatIf).length > 0 && (
        <Chip
          color="warning"
          label={t('portal.grades.whatIfActive', { count: Object.keys(whatIf).length })}
          onDelete={() => setWhatIf({})}
          sx={{ alignSelf: 'flex-start' }}
        />
      )}
    </Stack>
  );
}
